/* eslint-disable */
// File taken and modified from SBN:https://github.com/voxmedia/sbn/blob/master/app_unison/frontend/scripts/lib/iframeclient.js

import { isClient } from "../client";

export interface XFrameTypes {
  host: string;
  id: string;
  dispose: () => void;
  src: (src: string | Window | Element | null) => Window | null;
  post: (
    src: string | Element | Window | null,
    message: Record<string, any> | string,
    value?: any
  ) => void;
  request: (
    src: string | Element | Window | null,
    message: string,
    value?: any,
    callback?: () => void
  ) => string;
  end: (req: any, err?: any) => void;
  on: (
    message: string,
    handler: (e: any, data: any) => void,
    context: Record<string, any>
  ) => XFrameTypes;
  listen: () => XFrameTypes;
  stopListening: () => XFrameTypes;
}

export default class IframeClient {
  constructor() {
    if (!isClient()) {
      return;
    }
  }
  /**
   * Generates a random GUID (Globally-Unique IDentifier) component.
   */
  private s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }

  create(appId: string, originHost?: string): XFrameTypes {
    // Constants
    const CLIENT_GUID =
      this.s4() +
      this.s4() +
      "-" +
      this.s4() +
      "-" +
      this.s4() +
      "-" +
      this.s4() +
      "-" +
      this.s4() +
      this.s4() +
      this.s4();
    const PROTOCOL_APP = "@app";
    const PROTOCOL_APP_ID = appId || "xframe";
    const PROTOCOL_RESPONSE = "@res";
    const POLL_INTERVAL = 200;

    // Encapsulated state
    const handlers: any[] = [];
    const requests: Record<string, any> = {};
    const responses: Record<string, any> = {};
    let requestId = 0;
    let currentPoll: NodeJS.Timeout | null;
    let listener: {
      (evt: any): void;
      (this: Window, ev: MessageEvent<any>): any;
      (this: Window, ev: MessageEvent<any>): any;
    } | null = null;

    /**
     * Loops through all handlers, responding to the message type:
     * collects and returns an optional response value from handlers.
     */
    function handleMessage(
      evt: { origin: any; data: string; source: any },
      req: { message: any; value: any }
    ) {
      let res;
      for (let i = 0; i < handlers.length; i++) {
        const handler = handlers[i];
        if (handler.message === req.message) {
          res = handler.fn.call(handler.ctx, evt, req.value);
        }
      }
      return res;
    }

    return {
      host: originHost || "*",

      /**
       * READONLY
       * Specifies the GUID of the client instance.
       */
      get id() {
        return CLIENT_GUID;
      },

      /**
       * Resolves the source of a client window.
       * @param {String|Element|Window} src selector or iframe/window element.
       * @returns {Window} a resolved window source.
       * @private
       */
      src: function (src: string | Element | Window | null): Window | null {
        // Query for target when given a string:
        if (typeof src === "string") {
          src = document.querySelector(src) as Element;
        }

        // Check target element (iframe) for a content window:
        if (src instanceof HTMLIFrameElement && src.contentWindow) {
          src = src.contentWindow;
        }

        // Return a resolved source frame:
        if (typeof src === "string" || src instanceof Element) {
          return null;
        }
        return src && typeof src.postMessage === "function" ? src : null;
      },

      /**
       * Posts a message and value to a source window.
       * This is a blind post without guarentee that the message will go through
       * (messages may fail if the source is not yet fully initialized).
       * Use `request` to post a message with confirmation of receipt.
       * @param {String|Element|Window} src selector or iframe/window element.
       * @param {String|Object} message string or formatted object (w/ "message" key).
       * @param {Any} [value] an optional value to send with the message.
       */
      post: function (
        src: string | Element | Window | null,
        message: Record<string, any> | string,
        value?: any
      ) {
        const checkSrc = this.src(src);
        if (!checkSrc || !message) {
          throw Error("invalid post");
        }

        // Generate the base data object:
        const data =
          typeof message === "string" ? { message: message } : message;

        // Assign a value, if one was provided:
        if (value !== undefined) {
          data.value = value;
        }

        // Validate message and protocol before sending:
        if (data.message) {
          data[PROTOCOL_APP] = PROTOCOL_APP_ID;
          try {
            checkSrc.postMessage(JSON.stringify(data), this.host);
          } catch (e) {
            throw Error(JSON.stringify(e));
          }
        }
      },

      /**
       * Posts a message to a source with request for confirmation.
       * This implementation polls the source frame with the posted message
       * until the frame sends back a confirmation response.
       * Use this method to (better) guarentee delivery.
       * @param {String} message string to send.
       * @param {Any} [value] an optional value to send with the message.
       * @param {Function} [callback] function to call with the response data.
       * @returns {String} id of the newly-created request.
       */
      request: function (
        src: string | Element | Window | null,
        message: string,
        value?: any,
        callback?: () => void
      ): string {
        src = this.src(src);

        if (!src || !message) {
          throw Error("invalid request");
        }

        const self = this;
        const id = CLIENT_GUID + "-" + ("0000" + requestId++).slice(-4);
        const req = (requests[id] = {
          timeout: 15000,
          attempts: 0,
          src: src,
          cb: callback,
          data: {
            message: message,
            value: value,
            id: id,
          },
        });

        // Runs a single polling cycle to push pending messages.
        // Continues polling calls until the request queue is empty.
        function poll() {
          let running = false;

          for (const id in requests) {
            if (requests.hasOwnProperty(id)) {
              const pending = requests[id];
              if (pending.attempts++ < pending.timeout / POLL_INTERVAL) {
                self.post(pending.src, pending.data);
                running = true;
              } else {
                self.end(id, new Error("request timeout"));
              }
            }
          }

          currentPoll = running ? setTimeout(poll, POLL_INTERVAL) : null;
        }

        // Start polling, or else make an initial request:
        if (!currentPoll) {
          poll();
        } else {
          this.post(src, req.data);
        }
        return id;
      },

      /**
       * Ends a pending request.
       * The request is deleted from the queue,
       * and the pending request callback is fulfilled.
       * @param {String|Object} req id or data object to conclude.
       * @param {Error|Object} err or cancelation options.
       * Pass "abort: true" to end without response.
       * @example
       * xframe.end('request-0001', new Error('boom'))
       * xframe.end('request-0001', { abort: true })
       * xframe.end({ id: 'request-0001', value: 'hello' })
       */
      end: function (req: any, err?: any) {
        const id = typeof req === "string" ? req : req.id;
        const isAbort: boolean = err && err.abort;
        if (requests.hasOwnProperty(id)) {
          const pending = requests[id];
          delete requests[id];
          if (!isAbort && typeof pending.cb === "function") {
            pending.cb(err || null, req.value);
          }
        }
      },

      /**
       * Attaches a response handler to this XFrame Client.
       * Each handler may target a unique message type.
       * @param {String} message name to respond to.
       * @param {Function} handler function for responding.
       * Handler receives any value that was sent with the message,
       * and may return a value that gets sent back to requests.
       * @param {Object} context in which to invoke the handler.
       * @example
       * import xframe from 'xframe';
       * const client = xframe('volume', 'http://aweso.me');
       * client
       *  .on('play', function(data) { ... }, this)
       *  .on('pause', function(data) { ... }, this)
       *  .on('host', function(data) { return document.domain; }, this)
       *  .listen();
       */
      on: function (
        message: string,
        handler: (e: any, data: any) => void,
        context: Record<string, any>
      ) {
        handlers.push({ message: message, fn: handler, ctx: context });
        return this;
      },

      /**
       * Enables listening on the client.
       * Call this once after configuring all request handlers.
       */
      listen: function () {
        if (!listener) {
          const self = this;

          // Handle events sent from `postMessage`.
          // This listener delegates all requests and responses.
          listener = function (evt: {
            origin: any;
            data: string;
            source: any;
          }) {
            const origin =
              self.host === "*" || String(evt.origin).indexOf(self.host) >= 0;
            let req;

            // Parse request data:
            if (origin && /^\{.*\}$/.test(evt.data)) {
              try {
                req = JSON.parse(evt.data);
              } catch (e) {
                req = null;
              }
            }

            // Abort for invalid origin, request, or protocol:
            if (!origin || !req || req[PROTOCOL_APP] !== PROTOCOL_APP_ID) {
              return;
            }

            if (req.id) {
              // MESSAGE WITH ID (track request/response cycle)
              // Check if message is a response to a previous request:
              const isResponse = req.message === PROTOCOL_RESPONSE;

              // MESSAGE RESPONSE (conclude request/response cycle)
              if (isResponse && requests.hasOwnProperty(req.id)) {
                self.end(req);
              } else if (!isResponse && !responses.hasOwnProperty(req.id)) {
                // REQUEST FOR RESPONSE (handle message and send response)
                responses[req.id] = true;
                self.post(evt.source, {
                  message: PROTOCOL_RESPONSE,
                  value: handleMessage(evt, req) || "success",
                  id: req.id,
                });
              }
            } else {
              // GENERIC MESSAGE (just handle locally)
              handleMessage(evt, req);
            }
          };

          window.addEventListener("message", listener);
        }
        return this;
      },

      /**
       * Stops listening for message events.
       * Call this while uninstalling the client.
       */
      stopListening: function () {
        if (listener) {
          window.removeEventListener("message", listener);
          listener = null;
        }
        return this;
      },

      /**
       * Stops all pending requests.
       * Call this while uninstalling the client.
       */
      dispose: function () {
        if (currentPoll) {
          clearInterval(currentPoll);
          this.stopListening();
        }
      },
    };
  }
}
